/* eslint-disable fp/no-mutation */
/**
 * Communication layer between the Promote web app and the iOS native wrapper (WKWebView).
 *
 * Protocol (matches NikeNativeBridgeHandler.swift):
 *   Web → Native : window.webkit.messageHandlers.nikeNative.postMessage({ action })
 *   Native → Web : window.nikeNative.onAthleteToken({ token } | { error })
 *                   window.nikeNative.onUserDidChange({})
 */

const NATIVE_TOKEN_TIMEOUT_MS = 5000;

let sessionGeneration = 0;

const state = {
  bridgeInitialized: false,
  pendingGeneration: 0,
  tokenReject: null,
  tokenResolve: null,
};

const NativeBridge = {
  /**
   * Register the `window.nikeNative` callback object and background-resume listener.
   * Call once at app startup.
   *
   * @param {Object}   opts
   * @param {function} opts.onUserDidChange  - called when native detects a different SSO user
   * @param {function} opts.onTokenRefreshed - called with a fresh access_token on foreground resume
   */
  initialize({ onUserDidChange, onTokenRefreshed } = {}) {
    if (state.bridgeInitialized || !this.isNativeApp()) return;
    state.bridgeInitialized = true;

    window.nikeNative = {
      onAthleteToken: (response) => {
        if (state.pendingGeneration !== sessionGeneration) {
          state.tokenResolve = null;
          state.tokenReject = null;
          return;
        }
        if (response.error && state.tokenReject) {
          state.tokenReject(new Error(response.error));
        } else if (response.token && state.tokenResolve) {
          state.tokenResolve(response.token);
        }
        state.tokenResolve = null;
        state.tokenReject = null;
      },

      onUserDidChange: () => {
        sessionGeneration++;
        if (state.tokenReject) {
          state.tokenReject(new Error('Session invalidated by user change'));
        }
        state.tokenResolve = null;
        state.tokenReject = null;
        if (onUserDidChange) onUserDidChange();
      },
    };

    document.addEventListener('visibilitychange', () => {
      if (
        document.visibilityState === 'visible'
        && localStorage.getItem('isNativeApp') === 'true'
      ) {
        NativeBridge.requestAthleteToken()
          .then((token) => {
            if (onTokenRefreshed) onTokenRefreshed(token);
            return undefined;
          })
          .catch(() => { });
      }
    });
  },

  isNativeApp() {
    return !!(
      window.webkit
      && window.webkit.messageHandlers
      && window.webkit.messageHandlers.nikeNative
    );
  },

  /**
   * Ask native for the current athlete JWT.
   * Resolves with the raw access-token string; rejects on error or timeout.
   * If a prior request is still pending it is rejected before the new one starts.
   */
  requestAthleteToken() {
    return new Promise((resolve, reject) => {
      if (!this.isNativeApp()) {
        reject(new Error('Not running in native app'));
        return;
      }

      if (state.tokenReject) {
        state.tokenReject(new Error('Superseded by a newer token request'));
        state.tokenResolve = null;
        state.tokenReject = null;
      }

      const timeout = setTimeout(() => {
        state.tokenResolve = null;
        state.tokenReject = null;
        reject(new Error('Token request timed out'));
      }, NATIVE_TOKEN_TIMEOUT_MS);

      state.tokenResolve = (token) => {
        clearTimeout(timeout);
        resolve(token);
      };
      state.tokenReject = (err) => {
        clearTimeout(timeout);
        reject(err);
      };

      state.pendingGeneration = sessionGeneration;
      window.webkit.messageHandlers.nikeNative.postMessage({ action: 'getAthleteToken' });
    });
  },

  /** Tell native to log out (fire-and-forget; native tears down the WebView). */
  requestLogout() {
    if (!this.isNativeApp()) return;
    window.webkit.messageHandlers.nikeNative.postMessage({ action: 'logout' });
  },
};

export default NativeBridge;
